/**
 * ExternalPostsTab - Display posts fetched directly from Instagram/Facebook APIs
 *
 * Shows posts that were published on the platforms, including those not created through our system.
 * Features:
 * - Project selector to filter by connected accounts
 * - View toggle (Grid/Card) for both platforms
 * - Engagement metrics display (likes, comments, shares)
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../services/api';
import { useProjectsStore, Project } from '../store/projects';

// Types for Instagram media
interface InstagramMedia {
  id: string;
  caption?: string;
  mediaType: 'IMAGE' | 'VIDEO' | 'CAROUSEL_ALBUM';
  mediaUrl?: string;
  thumbnailUrl?: string;
  permalink: string;
  timestamp: string;
  likeCount?: number;
  commentsCount?: number;
}

// Types for Facebook posts
interface FacebookPost {
  id: string;
  message?: string;
  story?: string;
  createdTime: string;
  fullPicture?: string;
  permalinkUrl?: string;
  likes?: number;
  comments?: number;
  shares?: number;
}

interface FacebookPage {
  id: string;
  name: string;
  category?: string;
  accessToken?: string;
}

type Platform = 'instagram' | 'facebook';
type ViewMode = 'grid' | 'card';

interface ExternalPostsTabProps {
  className?: string;
}

export function ExternalPostsTab({ className }: ExternalPostsTabProps) {
  const { t } = useTranslation(['posts', 'common']);
  const { projects, fetchProjects } = useProjectsStore();
  const [selectedProjectId, setSelectedProjectId] = useState<string>('');
  const [activePlatform, setActivePlatform] = useState<Platform>('instagram');
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Instagram state
  const [instagramMedia, setInstagramMedia] = useState<InstagramMedia[]>([]);
  const [instagramPaging, setInstagramPaging] = useState<{ after?: string } | null>(null);
  const [instagramAccount, setInstagramAccount] = useState<{ username?: string; mediaCount?: number } | null>(null);

  // Facebook state
  const [facebookPages, setFacebookPages] = useState<FacebookPage[]>([]);
  const [selectedPageId, setSelectedPageId] = useState<string>('');
  const [facebookPosts, setFacebookPosts] = useState<FacebookPost[]>([]);
  const [facebookPaging, setFacebookPaging] = useState<{ after?: string } | null>(null);

  // Load projects on mount
  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  // Set default project when projects load
  useEffect(() => {
    if (projects.length > 0 && !selectedProjectId) {
      setSelectedProjectId(projects[0].id);
    }
  }, [projects, selectedProjectId]);

  // Load Instagram media
  const loadInstagramMedia = async (after?: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ limit: '25' });
      if (after) params.append('after', after);
      if (selectedProjectId) params.append('project_id', selectedProjectId);

      const response = await api.get<{
        success: boolean;
        media: InstagramMedia[];
        paging?: { cursors: { after: string } };
        error?: string;
        noAccount?: boolean;
      }>(`/instagram/media?${params}`);

      if (!response.success) {
        throw new Error(response.error || 'Failed to load Instagram media');
      }

      // Handle noAccount response - show empty state
      if (response.noAccount) {
        setInstagramMedia([]);
        setInstagramPaging(null);
        return;
      }

      if (after) {
        setInstagramMedia(prev => [...prev, ...response.media]);
      } else {
        setInstagramMedia(response.media || []);
      }
      setInstagramPaging(response.paging?.cursors ? { after: response.paging.cursors.after } : null);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('posts:external.errorLoadingInstagram'));
    } finally {
      setIsLoading(false);
    }
  };

  // Load Instagram account info
  const loadInstagramAccount = async () => {
    try {
      const params = selectedProjectId ? `?project_id=${selectedProjectId}` : '';
      const response = await api.get<{
        username?: string;
        mediaCount?: number;
        noAccount?: boolean;
      }>(`/instagram/account${params}`);

      if (response.noAccount) {
        setInstagramAccount(null);
        return;
      }

      setInstagramAccount(response);
    } catch (err) {
      console.error('Failed to load Instagram account:', err);
      setInstagramAccount(null);
    }
  };

  // Load Facebook pages
  const loadFacebookPages = async () => {
    try {
      const params = selectedProjectId ? `?project_id=${selectedProjectId}` : '';
      const response = await api.get<{ pages: FacebookPage[] }>(`/facebook/pages${params}`);
      setFacebookPages(response.pages || []);
      if (response.pages?.length > 0) {
        setSelectedPageId(response.pages[0].id);
      } else {
        setSelectedPageId('');
        setFacebookPosts([]);
      }
    } catch (err) {
      console.error('Failed to load Facebook pages:', err);
      setFacebookPages([]);
      setSelectedPageId('');
    }
  };

  // Load Facebook posts
  const loadFacebookPosts = async (pageId: string, after?: string) => {
    if (!pageId) return;

    setIsLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ limit: '25' });
      if (after) params.append('after', after);

      const response = await api.get<{
        success: boolean;
        posts: FacebookPost[];
        paging?: { cursors: { after: string } };
        error?: string;
      }>(`/facebook/pages/${pageId}/posts?${params}`);

      if (!response.success) {
        throw new Error(response.error || 'Failed to load Facebook posts');
      }

      if (after) {
        setFacebookPosts(prev => [...prev, ...response.posts]);
      } else {
        setFacebookPosts(response.posts);
      }
      setFacebookPaging(response.paging?.cursors ? { after: response.paging.cursors.after } : null);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('posts:external.errorLoadingFacebook'));
    } finally {
      setIsLoading(false);
    }
  };

  // Reload when project changes - reset all data and reload for current platform
  useEffect(() => {
    if (selectedProjectId) {
      // Reset all data when project changes
      setInstagramMedia([]);
      setInstagramAccount(null);
      setInstagramPaging(null);
      setFacebookPages([]);
      setFacebookPosts([]);
      setFacebookPaging(null);
      setSelectedPageId('');

      // Load data for current platform
      if (activePlatform === 'instagram') {
        loadInstagramAccount();
        loadInstagramMedia();
      } else {
        loadFacebookPages();
      }
    }
  }, [selectedProjectId]);

  // Reload when platform changes
  useEffect(() => {
    if (selectedProjectId) {
      if (activePlatform === 'instagram') {
        loadInstagramAccount();
        loadInstagramMedia();
      } else {
        loadFacebookPages();
      }
    }
  }, [activePlatform]);

  // Load Facebook posts when page is selected
  useEffect(() => {
    if (activePlatform === 'facebook' && selectedPageId) {
      loadFacebookPosts(selectedPageId);
    }
  }, [selectedPageId]);

  // Format date
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Format number with K/M suffix
  const formatNumber = (num?: number) => {
    if (num === undefined || num === null) return '0';
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  // Grid view icons
  const GridIcon = () => (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
    </svg>
  );

  const CardIcon = () => (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
    </svg>
  );

  // Render Instagram Grid View
  const renderInstagramGrid = () => (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
      {instagramMedia.map((media) => (
        <a
          key={media.id}
          href={media.permalink}
          target="_blank"
          rel="noopener noreferrer"
          className="group relative bg-gray-100 rounded-lg overflow-hidden aspect-square hover:ring-2 hover:ring-pink-500 transition-all"
        >
          <img
            src={media.thumbnailUrl || media.mediaUrl}
            alt={media.caption?.substring(0, 50) || 'Instagram post'}
            className="w-full h-full object-cover"
          />
          {media.mediaType === 'VIDEO' && (
            <div className="absolute top-2 right-2">
              <svg className="w-6 h-6 text-white drop-shadow-lg" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8 5v14l11-7z"/>
              </svg>
            </div>
          )}
          {media.mediaType === 'CAROUSEL_ALBUM' && (
            <div className="absolute top-2 right-2">
              <svg className="w-6 h-6 text-white drop-shadow-lg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
          )}
          <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-6 text-white">
            <div className="flex items-center gap-1">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
              </svg>
              <span className="font-medium">{formatNumber(media.likeCount)}</span>
            </div>
            <div className="flex items-center gap-1">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M21.99 4c0-1.1-.89-2-1.99-2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h14l4 4-.01-18z"/>
              </svg>
              <span className="font-medium">{formatNumber(media.commentsCount)}</span>
            </div>
          </div>
        </a>
      ))}
    </div>
  );

  // Render Instagram Card View
  const renderInstagramCard = () => (
    <div className="space-y-4">
      {instagramMedia.map((media) => (
        <a
          key={media.id}
          href={media.permalink}
          target="_blank"
          rel="noopener noreferrer"
          className="block bg-white border border-gray-200 rounded-lg p-4 hover:border-pink-300 hover:shadow-md transition-all"
        >
          <div className="flex gap-4">
            {(media.thumbnailUrl || media.mediaUrl) && (
              <div className="flex-shrink-0 w-24 h-24 rounded-lg overflow-hidden bg-gray-100 relative">
                <img
                  src={media.thumbnailUrl || media.mediaUrl}
                  alt=""
                  className="w-full h-full object-cover"
                />
                {media.mediaType === 'VIDEO' && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                    <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M8 5v14l11-7z"/>
                    </svg>
                  </div>
                )}
                {media.mediaType === 'CAROUSEL_ALBUM' && (
                  <div className="absolute top-1 right-1">
                    <svg className="w-5 h-5 text-white drop-shadow" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14" />
                    </svg>
                  </div>
                )}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-gray-800 line-clamp-3">
                {media.caption || <span className="text-gray-400 italic">{t('posts:external.noText')}</span>}
              </p>
              <div className="mt-2 flex items-center gap-4 text-sm text-gray-500">
                <span>{formatDate(media.timestamp)}</span>
                <span className="flex items-center gap-1 text-pink-600">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
                  </svg>
                  {formatNumber(media.likeCount)}
                </span>
                <span className="flex items-center gap-1">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M21.99 4c0-1.1-.89-2-1.99-2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h14l4 4-.01-18z"/>
                  </svg>
                  {formatNumber(media.commentsCount)}
                </span>
              </div>
            </div>
          </div>
        </a>
      ))}
    </div>
  );

  // Render Facebook Grid View
  const renderFacebookGrid = () => (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
      {facebookPosts.map((post) => (
        <a
          key={post.id}
          href={post.permalinkUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="group relative bg-gray-100 rounded-lg overflow-hidden aspect-square hover:ring-2 hover:ring-blue-500 transition-all"
        >
          {post.fullPicture ? (
            <img
              src={post.fullPicture}
              alt={post.message?.substring(0, 50) || 'Facebook post'}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center p-4 bg-gradient-to-br from-blue-50 to-blue-100">
              <p className="text-sm text-gray-600 line-clamp-6 text-center">
                {post.message || post.story || t('posts:external.noText')}
              </p>
            </div>
          )}
          <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-4 text-white">
            <div className="flex items-center gap-1">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M2 21h2v-8H2v8zm20-7c0-.55-.45-1-1-1h-6.31l.95-4.57.03-.32c0-.41-.17-.79-.44-1.06L14.17 6 7.58 12.59c-.37.36-.58.86-.58 1.41v7h11.83c.75 0 1.41-.41 1.75-1.03l2.87-6.71c.08-.2.13-.42.13-.66v-1.6l-.02-.01.02-.99z"/>
              </svg>
              <span className="font-medium">{formatNumber(post.likes)}</span>
            </div>
            <div className="flex items-center gap-1">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M21.99 4c0-1.1-.89-2-1.99-2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h14l4 4-.01-18z"/>
              </svg>
              <span className="font-medium">{formatNumber(post.comments)}</span>
            </div>
            {post.shares !== undefined && post.shares > 0 && (
              <div className="flex items-center gap-1">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                </svg>
                <span className="font-medium">{formatNumber(post.shares)}</span>
              </div>
            )}
          </div>
        </a>
      ))}
    </div>
  );

  // Render Facebook Card View
  const renderFacebookCard = () => (
    <div className="space-y-4">
      {facebookPosts.map((post) => (
        <a
          key={post.id}
          href={post.permalinkUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="block bg-white border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:shadow-md transition-all"
        >
          <div className="flex gap-4">
            {post.fullPicture && (
              <div className="flex-shrink-0 w-24 h-24 rounded-lg overflow-hidden bg-gray-100">
                <img
                  src={post.fullPicture}
                  alt=""
                  className="w-full h-full object-cover"
                />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-gray-800 line-clamp-3">
                {post.message || post.story || <span className="text-gray-400 italic">{t('posts:external.noText')}</span>}
              </p>
              <div className="mt-2 flex items-center gap-4 text-sm text-gray-500">
                <span>{formatDate(post.createdTime)}</span>
                <span className="flex items-center gap-1 text-blue-600">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M2 21h2v-8H2v8zm20-7c0-.55-.45-1-1-1h-6.31l.95-4.57.03-.32c0-.41-.17-.79-.44-1.06L14.17 6 7.58 12.59c-.37.36-.58.86-.58 1.41v7h11.83c.75 0 1.41-.41 1.75-1.03l2.87-6.71c.08-.2.13-.42.13-.66v-1.6l-.02-.01.02-.99z"/>
                  </svg>
                  {formatNumber(post.likes)}
                </span>
                <span className="flex items-center gap-1">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M21.99 4c0-1.1-.89-2-1.99-2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h14l4 4-.01-18z"/>
                  </svg>
                  {formatNumber(post.comments)}
                </span>
                {post.shares !== undefined && post.shares > 0 && (
                  <span className="flex items-center gap-1">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                    </svg>
                    {formatNumber(post.shares)}
                  </span>
                )}
              </div>
            </div>
          </div>
        </a>
      ))}
    </div>
  );

  return (
    <div className={className}>
      {/* Header with Project Selector, Platform Tabs, and View Toggle */}
      <div className="flex flex-wrap items-center gap-4 mb-6">
        {/* Project Selector */}
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-700">{t('common:labels.project')}:</label>
          <select
            value={selectedProjectId}
            onChange={(e) => setSelectedProjectId(e.target.value)}
            className="text-sm border border-gray-300 rounded-md px-3 py-1.5 bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            {projects.map((project: Project) => (
              <option key={project.id} value={project.id}>
                {project.name}
              </option>
            ))}
          </select>
        </div>

        {/* Platform Tabs */}
        <div className="flex bg-gray-100 rounded-lg p-1">
          <button
            onClick={() => setActivePlatform('instagram')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activePlatform === 'instagram'
                ? 'bg-white text-pink-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <span className="flex items-center gap-2">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073z"/>
                <path d="M12 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8z"/>
                <circle cx="18.406" cy="5.594" r="1.44"/>
              </svg>
              Instagram
            </span>
          </button>
          <button
            onClick={() => setActivePlatform('facebook')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activePlatform === 'facebook'
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <span className="flex items-center gap-2">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
              Facebook
            </span>
          </button>
        </div>

        {/* View Toggle */}
        <div className="flex bg-gray-100 rounded-lg p-1 ml-auto">
          <button
            onClick={() => setViewMode('grid')}
            className={`p-2 rounded-md transition-colors ${
              viewMode === 'grid'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            title={t('posts:external.gridView')}
          >
            <GridIcon />
          </button>
          <button
            onClick={() => setViewMode('card')}
            className={`p-2 rounded-md transition-colors ${
              viewMode === 'card'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            title={t('posts:external.cardView')}
          >
            <CardIcon />
          </button>
        </div>
      </div>

      {/* Account/Page info row */}
      <div className="flex items-center gap-4 mb-4">
        {activePlatform === 'instagram' && instagramAccount?.username && (
          <div className="text-sm text-gray-600">
            <span className="font-medium">@{instagramAccount.username}</span>
            <span className="mx-2">·</span>
            <span>{instagramAccount.mediaCount} Posts</span>
          </div>
        )}

        {activePlatform === 'facebook' && facebookPages.length > 0 && (
          <div className="text-sm text-gray-600">
            <span className="font-medium">
              {facebookPages.find(p => p.id === selectedPageId)?.name || facebookPages[0]?.name}
            </span>
            <span className="mx-2">·</span>
            <span>{facebookPosts.length} Posts</span>
          </div>
        )}
      </div>

      {/* Error message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      )}

      {/* Loading state */}
      {isLoading && instagramMedia.length === 0 && facebookPosts.length === 0 && (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      )}

      {/* Instagram Content */}
      {activePlatform === 'instagram' && (
        <>
          {viewMode === 'grid' ? renderInstagramGrid() : renderInstagramCard()}

          {instagramPaging?.after && (
            <div className="mt-6 text-center">
              <button
                onClick={() => loadInstagramMedia(instagramPaging.after)}
                disabled={isLoading}
                className="px-6 py-2 bg-pink-600 text-white rounded-lg hover:bg-pink-700 disabled:opacity-50"
              >
                {isLoading ? t('common:status.loading') : t('common:actions.loadMore')}
              </button>
            </div>
          )}

          {!isLoading && instagramMedia.length === 0 && !error && !instagramAccount && (
            <div className="text-center py-12 text-gray-500">
              <p>{t('posts:external.noInstagramAccount')}</p>
              <p className="text-sm mt-2">{t('posts:external.connectInstagram')}</p>
            </div>
          )}

          {!isLoading && instagramMedia.length === 0 && !error && instagramAccount && (
            <div className="text-center py-12 text-gray-500">
              <p>{t('posts:external.noInstagramPosts')}</p>
            </div>
          )}
        </>
      )}

      {/* Facebook Content */}
      {activePlatform === 'facebook' && (
        <>
          {viewMode === 'grid' ? renderFacebookGrid() : renderFacebookCard()}

          {!isLoading && facebookPosts.length === 0 && selectedPageId && (
            <div className="text-center py-12 text-gray-500">
              {t('posts:external.noFacebookPosts')}
            </div>
          )}

          {!isLoading && !selectedPageId && facebookPages.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              <p>{t('posts:external.noFacebookAccount')}</p>
              <p className="text-sm mt-2">{t('posts:external.connectFacebook')}</p>
            </div>
          )}

          {facebookPaging?.after && (
            <div className="mt-6 text-center">
              <button
                onClick={() => loadFacebookPosts(selectedPageId, facebookPaging.after)}
                disabled={isLoading}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {isLoading ? t('common:status.loading') : t('common:actions.loadMore')}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
